import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import MyDeskWidgetCard from './MyDeskWidgetCard';
import useFlip, { nextFrame, prefersReducedMotion } from './useFlip';
import {
  moveItem,
  orderByIds,
  pickSlot,
  projectOrder,
  sizeForSpan,
  verticalTarget,
  visibleSpan,
} from './layoutMath';

const INSTRUCTIONS_ID = 'mydesk-move-instructions';
const DEFAULT_GAP = 20;
const DROP_MS = 300;
const ADD_TILE_ID = '__add';
const SIZE_WORDS = { third: 'one column wide', two_thirds: 'two columns wide', full: 'full width' };

// dnd-kit runs the pointer and touch side of a drag: activation after a few
// pixels, Escape to cancel and auto-scroll near the edges. Its announcements
// describe droppable areas, which this board does not use, so they are off
// and the board announces card positions itself.
const ACCESSIBILITY = {
  announcements: {
    onDragStart: () => undefined,
    onDragOver: () => undefined,
    onDragEnd: () => undefined,
    onDragCancel: () => undefined,
  },
  screenReaderInstructions: { draggable: '' },
};

// A drag starts anywhere on a card header except on its buttons (the grip
// excepted), links and form fields.
class HeaderPointerSensor extends PointerSensor {
  static activators = [{
    eventName: 'onPointerDown',
    handler: ({ nativeEvent: event }) => {
      if (!event.isPrimary || event.button !== 0) return false;
      const { target } = event;
      return !(target instanceof Element && target.closest('a, input, select, textarea, button:not(.mydesk-grip)'));
    },
  }];
}

const cardNode = (grid, id) => Array.from(grid.children).find((node) => node.dataset && node.dataset.flipId === id);

const gridMetrics = (grid) => {
  const style = window.getComputedStyle(grid);
  const template = style.gridTemplateColumns;
  const columns = template && template !== 'none' ? template.split(' ').filter(Boolean).length : 3;
  const gap = parseFloat(style.columnGap) || DEFAULT_GAP;
  const width = grid.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
  return { columns, gap, width, columnWidth: (width - gap * (columns - 1)) / columns };
};

// Layout positions of the cards in display order. Offsets ignore transforms,
// so a card still sliding into place is measured where it will end up.
const flowRects = (grid) => {
  const origin = grid.getBoundingClientRect();
  return Array.from(grid.children)
    .filter((node) => node.dataset && node.dataset.flipId && node.dataset.flipId !== ADD_TILE_ID)
    .map((node) => ({
      id: node.dataset.flipId,
      left: origin.left + node.offsetLeft,
      top: origin.top + node.offsetTop,
      right: origin.left + node.offsetLeft + node.offsetWidth,
      bottom: origin.top + node.offsetTop + node.offsetHeight,
    }));
};

/**
 * Three-column board with live reflow. While a card is dragged by its header
 * it stays in the grid as a dashed placeholder at the slot under the pointer,
 * a snapshot of it follows the pointer, and every other card slides into the
 * place it will have after the drop. The grip is also a keyboard control and
 * the green edge handle resizes a card in whole columns with the same preview.
 */
const MyDeskGrid = ({
  widgets,
  catalogByType,
  loading,
  editMode,
  onReorder,
  onResize,
  onRemove,
  onOpenDrawer,
  editorRef,
  announce,
}) => {
  const gridRef = useRef(null);
  const capture = useFlip(gridRef);
  const sensors = useSensors(useSensor(HeaderPointerSensor, { activationConstraint: { distance: 5 } }));

  const [drag, setDrag] = useState(null); // { id, index, origin }
  const [dropping, setDropping] = useState(false);
  const [overlayBox, setOverlayBox] = useState(null);
  const [kb, setKbState] = useState(null); // { id, origin, order }
  const [sizePreview, setSizePreview] = useState(null); // { id, size }
  const [resizingId, setResizingId] = useState(null);

  const dragRef = useRef(null);
  const kbRef = useRef(null);
  const busyRef = useRef(false);
  const frameRef = useRef(null);
  const overlayRef = useRef(null);
  const cloneRef = useRef(null);
  const titlesRef = useRef(new Map());

  const setKb = useCallback((next) => {
    kbRef.current = next;
    setKbState(next);
  }, []);

  const registerTitle = useCallback((id, title) => { titlesRef.current.set(id, title); }, []);
  const titleFor = useCallback((id) => {
    const widget = widgets.find((w) => w.id === id);
    return titlesRef.current.get(id) || catalogByType[widget?.type]?.title || 'Widget';
  }, [widgets, catalogByType]);

  useEffect(() => {
    if (!editorRef) return undefined;
    editorRef.current = {
      capture,
      isBusy: () => Boolean(busyRef.current || dragRef.current || kbRef.current),
    };
    return () => { editorRef.current = null; };
  }, [editorRef, capture]);

  const display = useMemo(() => {
    let list = widgets;
    if (kb) list = orderByIds(widgets, kb.order);
    else if (drag) list = projectOrder(widgets, drag.id, drag.index);
    if (sizePreview) list = list.map((w) => (w.id === sizePreview.id ? { ...w, size: sizePreview.size } : w));
    return list;
  }, [widgets, drag, kb, sizePreview]);

  /* ---------- keyboard: Space picks up, arrows move, Space drops ---------- */

  const dropKb = useCallback((cancelled) => {
    const k = kbRef.current;
    if (!k) return;
    const title = titleFor(k.id);
    const index = k.order.indexOf(k.id);
    const count = k.order.length;
    if (cancelled) {
      if (index !== k.origin) capture();
      announce(`Cancelled. ${title} is back at position ${k.origin + 1} of ${count}.`);
    } else {
      if (index !== k.origin) onReorder(orderByIds(widgets, k.order), { label: `Moved ${title}` });
      announce(`Dropped ${title} at position ${index + 1} of ${count}.`);
    }
    setKb(null);
  }, [widgets, capture, onReorder, announce, titleFor, setKb]);

  const handleGripKey = useCallback((id, event) => {
    const { key } = event;
    const k = kbRef.current;
    if (!k) {
      if (key === ' ' || key === 'Enter') {
        event.preventDefault();
        const order = widgets.map((w) => w.id);
        const origin = order.indexOf(id);
        setKb({ id, origin, order });
        announce(`Picked up ${titleFor(id)}. Position ${origin + 1} of ${order.length}. Use the arrow keys to move it, Space to drop it, Escape to cancel.`);
      }
      return;
    }
    if (k.id !== id) return;
    if (key === ' ' || key === 'Enter') {
      event.preventDefault();
      dropKb(false);
      return;
    }
    if (key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      dropKb(true);
      return;
    }
    if (!key.startsWith('Arrow')) return;
    event.preventDefault();
    const index = k.order.indexOf(id);
    let target = index;
    if (key === 'ArrowLeft') target = Math.max(0, index - 1);
    else if (key === 'ArrowRight') target = Math.min(k.order.length - 1, index + 1);
    else if (gridRef.current) target = verticalTarget(flowRects(gridRef.current), index, key === 'ArrowUp');
    if (target === index) return;
    capture();
    setKb({ ...k, order: moveItem(k.order, index, target) });
    announce(`${titleFor(id)} moved to position ${target + 1} of ${k.order.length}.`);
  }, [widgets, capture, announce, titleFor, dropKb, setKb]);

  // Leaving the grip, or leaving edit mode, drops the card where it is.
  const handleGripBlur = useCallback((id) => {
    setTimeout(() => {
      const k = kbRef.current;
      if (!k || k.id !== id) return;
      const node = gridRef.current && cardNode(gridRef.current, id);
      const grip = node && node.querySelector('.mydesk-grip');
      if (grip && document.activeElement === grip) return;
      dropKb(false);
    }, 0);
  }, [dropKb]);

  useEffect(() => {
    if (!editMode && kbRef.current) dropKb(false);
  }, [editMode, dropKb]);

  // Moving a card moves its DOM node, which can drop focus: keep it on the grip.
  useLayoutEffect(() => {
    if (!kb || !gridRef.current) return;
    const node = cardNode(gridRef.current, kb.id);
    const grip = node && node.querySelector('.mydesk-grip');
    if (grip && document.activeElement !== grip) grip.focus();
  }, [kb]);

  /* ---------- pointer and touch drag ---------- */

  const evaluate = useCallback(() => {
    const d = dragRef.current;
    const grid = gridRef.current;
    if (!d || !grid) return;
    if (overlayRef.current) {
      overlayRef.current.style.transform = `translate(${d.x - d.startX}px, ${d.y - d.startY}px)${prefersReducedMotion() ? '' : ' scale(1.02)'}`;
    }
    // After a reflow the layout under a still pointer has changed, so check
    // once more; two moves per pointer position at most, so two layouts
    // that disagree can never ping-pong.
    const samePos = Math.abs(d.x - d.last.x) < 1 && Math.abs(d.y - d.last.y) < 1 && d.last.scrolls === d.scrolls;
    if (samePos && d.last.moves >= 2) return;
    const rects = flowRects(grid);
    const here = rects.findIndex((r) => r.id === d.id);
    if (here < 0) return;
    const { width, gap } = gridMetrics(grid);
    const target = pickSlot(rects, d.x, d.y, here, width, gap);
    if (target === null || target === here) return;
    capture();
    d.index = target;
    d.last = samePos ? { ...d.last, moves: d.last.moves + 1 } : { x: d.x, y: d.y, scrolls: d.scrolls, moves: 1 };
    d.recheck = d.last.moves < 2;
    setDrag({ id: d.id, index: target, origin: d.origin });
  }, [capture]);

  const schedule = useCallback(() => {
    if (frameRef.current) return;
    frameRef.current = nextFrame(() => {
      frameRef.current = null;
      evaluate();
    });
  }, [evaluate]);

  useLayoutEffect(() => {
    const d = dragRef.current;
    if (d && d.recheck) {
      d.recheck = false;
      schedule();
    }
  }, [drag, schedule]);

  // Scrolling (auto-scroll included) moves the cards under a still pointer.
  const tracking = Boolean(drag) && !dropping;
  useEffect(() => {
    if (!tracking) return undefined;
    const onScroll = () => {
      const d = dragRef.current;
      if (!d) return;
      d.scrolls += 1;
      schedule();
    };
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      if (frameRef.current) {
        frameRef.current();
        frameRef.current = null;
      }
    };
  }, [tracking, schedule]);

  // The grid follows the pointer itself from the moment a header is pressed.
  // dnd-kit reports a drop only once its own state has rendered after the
  // drag started, so a quick flick could end without the grid hearing of it;
  // the grid therefore drops on its own pointer-up and cancels on its own
  // Escape. A later report from dnd-kit then finds nothing left to do.
  const pointerRef = useRef(null);
  const detachPointerRef = useRef(null);
  const finishDragRef = useRef(null);

  const trackPointer = useCallback((event) => {
    if (!event.isPrimary || event.button !== 0) return;
    const { target } = event;
    if (target instanceof Element && target.closest('a, input, select, textarea, button:not(.mydesk-grip)')) return;
    if (detachPointerRef.current) detachPointerRef.current();

    function detach() {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp, true);
      window.removeEventListener('pointercancel', onCancel, true);
      window.removeEventListener('keydown', onKey, true);
      detachPointerRef.current = null;
    }
    function follow(e) {
      pointerRef.current = { x: e.clientX, y: e.clientY };
      const d = dragRef.current;
      if (d) {
        d.x = e.clientX;
        d.y = e.clientY;
      }
    }
    function onMove(e) {
      follow(e);
      if (dragRef.current) schedule();
    }
    function onUp(e) {
      follow(e);
      detach();
      if (dragRef.current && finishDragRef.current) finishDragRef.current(false);
    }
    function onCancel() {
      detach();
      if (dragRef.current && finishDragRef.current) finishDragRef.current(true);
    }
    function onKey(e) {
      if (e.key !== 'Escape' || !dragRef.current || !finishDragRef.current) return;
      detach();
      finishDragRef.current(true);
    }

    pointerRef.current = { x: event.clientX, y: event.clientY };
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('pointercancel', onCancel, true);
    window.addEventListener('keydown', onKey, true);
    detachPointerRef.current = detach;
  }, [schedule]);

  useEffect(() => () => {
    if (detachPointerRef.current) detachPointerRef.current();
  }, []);

  const handleDragStart = useCallback(({ active, activatorEvent }) => {
    const grid = gridRef.current;
    const node = grid && cardNode(grid, active.id);
    if (!node) return;
    if (kbRef.current) dropKb(false);
    const rect = node.getBoundingClientRect();
    const origin = widgets.findIndex((w) => w.id === active.id);
    const startX = typeof activatorEvent?.clientX === 'number' ? activatorEvent.clientX : rect.left + rect.width / 2;
    const startY = typeof activatorEvent?.clientY === 'number' ? activatorEvent.clientY : rect.top + rect.height / 2;
    // The pointer has already travelled past the activation distance.
    const now = detachPointerRef.current && pointerRef.current ? pointerRef.current : { x: startX, y: startY };

    // The lifted card is a snapshot of the real one, so the widget does not
    // mount twice and fetch its data again. The real card stays in the grid
    // as the placeholder.
    const clone = node.cloneNode(true);
    clone.removeAttribute('data-flip-id');
    clone.removeAttribute('style');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    cloneRef.current = clone;

    dragRef.current = {
      id: active.id,
      origin,
      index: origin,
      x: now.x,
      y: now.y,
      startX,
      startY,
      scrolls: 0,
      last: { x: NaN, y: NaN, scrolls: -1, moves: 0 },
      recheck: false,
    };
    busyRef.current = true;
    setDropping(false);
    setOverlayBox({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    setDrag({ id: active.id, index: origin, origin });
    announce(`Picked up ${titleFor(active.id)}. Position ${origin + 1} of ${widgets.length}.`);
    schedule();
  }, [widgets, announce, titleFor, dropKb, schedule]);

  const finishDrag = useCallback((cancelled) => {
    // Settle on the slot under the final pointer position first.
    if (!cancelled && dragRef.current) evaluate();
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    const title = titleFor(d.id);
    const count = widgets.length;
    const moved = d.index !== d.origin;
    if (moved && cancelled) {
      capture();
      setDrag({ id: d.id, index: d.origin, origin: d.origin });
    }
    if (moved && !cancelled) onReorder(projectOrder(widgets, d.id, d.index), { label: `Moved ${title}` });
    announce(cancelled
      ? `Cancelled. ${title} is back at position ${d.origin + 1} of ${count}.`
      : `Dropped ${title} at position ${d.index + 1} of ${count}.`);
    setDropping(true);
  }, [widgets, capture, onReorder, announce, titleFor, evaluate]);

  useEffect(() => { finishDragRef.current = finishDrag; }, [finishDrag]);

  const mountOverlay = useCallback((el) => {
    overlayRef.current = el;
    if (el && cloneRef.current && el.firstChild !== cloneRef.current) el.replaceChildren(cloneRef.current);
  }, []);

  // Drop: the snapshot flies into the card's slot, then the card shows again.
  useLayoutEffect(() => {
    if (!dropping) return undefined;
    const done = () => {
      cloneRef.current = null;
      overlayRef.current = null;
      busyRef.current = false;
      setOverlayBox(null);
      setDrag(null);
      setDropping(false);
    };
    const grid = gridRef.current;
    const overlay = overlayRef.current;
    const node = grid && drag && cardNode(grid, drag.id);
    if (!grid || !overlay || !node || !overlayBox || prefersReducedMotion()) {
      done();
      return undefined;
    }
    const origin = grid.getBoundingClientRect();
    const dx = origin.left + node.offsetLeft - overlayBox.left;
    const dy = origin.top + node.offsetTop - overlayBox.top;
    overlay.classList.add('is-dropping');
    const cancelFrame = nextFrame(() => { overlay.style.transform = `translate(${dx}px, ${dy}px)`; });
    const timer = setTimeout(done, DROP_MS);
    return () => {
      cancelFrame();
      clearTimeout(timer);
    };
  }, [dropping, drag, overlayBox]);

  /* ---------- resize: edge handle, size buttons, arrow keys ---------- */

  const resizeApi = useMemo(() => {
    const find = (id) => widgets.find((w) => w.id === id);
    const commit = (id, size) => {
      onResize(id, size, { label: `Resized ${titleFor(id)}` });
      announce(`${titleFor(id)} is now ${SIZE_WORDS[size] || size}.`);
    };
    return {
      start: (id) => {
        const grid = gridRef.current;
        const widget = find(id);
        if (!grid || !widget) return null;
        const metrics = gridMetrics(grid);
        if (metrics.columns < 2) return null;
        if (kbRef.current) dropKb(false);
        setResizingId(id);
        return { ...metrics, span: visibleSpan(widget.size, metrics.columns) };
      },
      preview: (id, span, columns) => {
        const widget = find(id);
        if (!widget) return;
        const size = sizeForSpan(span, widget.size, columns);
        capture();
        setSizePreview(size === widget.size ? null : { id, size });
      },
      end: (id, span, columns) => {
        const widget = find(id);
        capture();
        setResizingId(null);
        setSizePreview(null);
        if (!widget) return;
        const size = sizeForSpan(span, widget.size, columns);
        if (size !== widget.size) commit(id, size);
      },
      step: (id, delta) => {
        const grid = gridRef.current;
        const widget = find(id);
        if (!grid || !widget) return;
        const { columns } = gridMetrics(grid);
        const current = visibleSpan(widget.size, columns);
        const span = Math.max(1, Math.min(columns, current + delta));
        if (span === current) return;
        capture();
        commit(id, sizeForSpan(span, widget.size, columns));
      },
      setSize: (id, size) => {
        const widget = find(id);
        if (!widget || widget.size === size) return;
        capture();
        commit(id, size);
      },
    };
  }, [widgets, capture, onResize, announce, titleFor, dropKb]);

  const handleRemove = useCallback((id) => {
    const title = titleFor(id);
    // A card lifted with the keyboard is dropped where it is first, so its
    // list and the announced positions never include the removed card.
    if (kbRef.current) {
      if (kbRef.current.id === id) setKb(null);
      else dropKb(false);
    }
    capture();
    onRemove(id, { label: `Removed ${title}` });
    announce(editMode ? `Removed ${title}. Undo brings it back.` : `Removed ${title}.`);
  }, [capture, onRemove, announce, titleFor, editMode, setKb, dropKb]);

  if (loading) {
    return (
      <div className="mydesk-grid" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="mydesk-card mydesk-card-skeleton" data-size="third" />
        ))}
      </div>
    );
  }

  if (widgets.length === 0) {
    return (
      <div className="mydesk-empty">
        <h3>Your workspace is empty</h3>
        <p>Add the widgets you want to see first thing every morning.</p>
        <button type="button" className="mydesk-btn mydesk-btn-primary" onClick={onOpenDrawer}>
          <FontAwesomeIcon icon={faPlus} />
          Add widget
        </button>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      accessibility={ACCESSIBILITY}
      onDragStart={handleDragStart}
      onDragEnd={() => finishDrag(false)}
      onDragCancel={() => finishDrag(true)}
    >
      <div className={`mydesk-grid ${drag ? 'is-dragging' : ''}`} ref={gridRef}>
        {display.map((widget) => (
          <MyDeskWidgetCard
            key={widget.id}
            widget={widget}
            catalogEntry={catalogByType[widget.type]}
            editMode={editMode}
            isPlaceholder={Boolean(drag) && drag.id === widget.id}
            isKbLifted={Boolean(kb) && kb.id === widget.id}
            isResizing={resizingId === widget.id}
            onRemove={handleRemove}
            onGripKey={handleGripKey}
            onGripBlur={handleGripBlur}
            onHeaderPointerDown={trackPointer}
            resizeApi={resizeApi}
            registerTitle={registerTitle}
            instructionsId={INSTRUCTIONS_ID}
          />
        ))}
        {editMode && (
          <button type="button" className="mydesk-add-tile" data-flip-id={ADD_TILE_ID} onClick={onOpenDrawer}>
            <FontAwesomeIcon icon={faPlus} />
            Add widget
          </button>
        )}
      </div>
      <p id={INSTRUCTIONS_ID} className="mydesk-sr-only">
        Drag the card by its header, or press Space to pick it up, the arrow keys to move it, Space to drop it and Escape to cancel.
      </p>
      {overlayBox && createPortal(
        <div
          ref={mountOverlay}
          className="mydesk-drag-overlay"
          style={{ left: overlayBox.left, top: overlayBox.top, width: overlayBox.width, height: overlayBox.height }}
          aria-hidden="true"
        />,
        document.body
      )}
    </DndContext>
  );
};

export default MyDeskGrid;
